import type { SvgIconComponent } from '@mui/icons-material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRunOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCutOutlined';
import MusicNoteIcon from '@mui/icons-material/MusicNoteOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughSOutlined';
import SmartphoneIcon from '@mui/icons-material/SmartphoneOutlined';

/**
 * The single source of truth for annotation categories: the studio's picker,
 * timeline marker colours, the editor's feedback list and the marker exports
 * all read from here, so a "Dialogue" note looks the same everywhere.
 *
 * Ids are what is stored on each annotation (`category`) and must not change.
 * Colours are dark, muted hues with at least 4.5:1 contrast on white, so they
 * work as text, as marker fills, and behind white labels. Each hue family maps
 * to a DaVinci Resolve marker colour in the EDL export, so a blue note in
 * PithPlay arrives as a blue marker in Resolve: change a shade, never the
 * family. The two loudest were toned down (purple #7C3AED to plum, royal blue
 * #1D4ED8 to a calmer cobalt). The previous palette put Dialogue (#0D9488) and
 * Music (#0F766E) on two near-identical teals and Action (#F59E0B) below 2:1
 * on white.
 */

export interface AnnotationCategory {
  id: string;
  label: string;
  color: string;
  Icon: SvgIconComponent;
  /** DaVinci Resolve marker colour name, used in the EDL export. */
  resolveColor: string;
  /** CMX3600 locator colour, used in the EDL export. */
  locatorColor: string;
}

export interface ResolvedAnnotationCategory extends AnnotationCategory {
  /** False for a value that is not one of our categories (the six note categories, Remove and Short). */
  known: boolean;
}

export const DEFAULT_CATEGORY_ID = 'general';

export const ANNOTATION_CATEGORIES: readonly AnnotationCategory[] = [
  { id: 'general', label: 'General', color: '#B45309', Icon: ChatBubbleOutlineIcon, resolveColor: 'ResolveColorYellow', locatorColor: 'YELLOW' },
  { id: 'dialogue', label: 'Dialogue', color: '#2C58AF', Icon: RecordVoiceOverIcon, resolveColor: 'ResolveColorBlue', locatorColor: 'BLUE' },
  { id: 'action', label: 'Action', color: '#BE123C', Icon: DirectionsRunIcon, resolveColor: 'ResolveColorRed', locatorColor: 'RED' },
  { id: 'scene', label: 'Scene Change', color: '#15803D', Icon: ContentCutIcon, resolveColor: 'ResolveColorGreen', locatorColor: 'GREEN' },
  { id: 'music', label: 'Music/Audio', color: '#0E7490', Icon: MusicNoteIcon, resolveColor: 'ResolveColorCyan', locatorColor: 'CYAN' },
  { id: 'visual', label: 'Visual Effect', color: '#6B4C9A', Icon: LayersOutlinedIcon, resolveColor: 'ResolveColorPurple', locatorColor: 'MAGENTA' },
];

/**
 * The creator's directions, marked on the transcript: words to take out of
 * the edit, and stretches to make into shorts. They are stored, shown and
 * exported like any other category, but they are not offered in the note
 * category picker: each one needs a range, and the transcript gives it
 * word-exact times. Remove is a dark, neutral grey (removed words are struck
 * through and greyed out, and it stays apart from every hue above); Short is
 * a magenta, bluer than Action and pinker than Visual Effect.
 */
export const DIRECTION_CATEGORIES: readonly AnnotationCategory[] = [
  { id: 'remove', label: 'Remove', color: '#3F3F46', Icon: StrikethroughSIcon, resolveColor: 'ResolveColorCocoa', locatorColor: 'BLACK' },
  { id: 'short', label: 'Short', color: '#A21C88', Icon: SmartphoneIcon, resolveColor: 'ResolveColorFuchsia', locatorColor: 'MAGENTA' },
];

/** Every category we know, the note categories first. */
export const ALL_ANNOTATION_CATEGORIES: readonly AnnotationCategory[] = [...ANNOTATION_CATEGORIES, ...DIRECTION_CATEGORIES];

export type DirectionCategoryId = 'remove' | 'short';

/** Neutral slate for values we cannot place and that carry no usable colour. */
export const UNKNOWN_CATEGORY_COLOR = '#475569';

const BY_ID = new Map(ALL_ANNOTATION_CATEGORIES.map((category) => [category.id, category]));

/**
 * Older clients, the multicam annotator and imports used labels or other
 * spellings ("Scene Change", "audio", "vfx"). Keys are lower-case with
 * everything but letters and digits removed.
 */
const ALIASES: Record<string, string> = {
  general: 'general',
  note: 'general',
  notes: 'general',
  comment: 'general',
  other: 'general',
  dialogue: 'dialogue',
  dialog: 'dialogue',
  speech: 'dialogue',
  voice: 'dialogue',
  action: 'action',
  pacing: 'action',
  scene: 'scene',
  scenechange: 'scene',
  cut: 'scene',
  transition: 'scene',
  music: 'music',
  musicaudio: 'music',
  audio: 'music',
  sound: 'music',
  sfx: 'music',
  visual: 'visual',
  visualeffect: 'visual',
  visualeffects: 'visual',
  vfx: 'visual',
  effect: 'visual',
  effects: 'visual',
  remove: 'remove',
  removed: 'remove',
  short: 'short',
  shorts: 'short',
};

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A stored colour we can safely put in CSS, or null. */
export const safeHexColor = (value?: string | null): string | null =>
  typeof value === 'string' && HEX_COLOR.test(value.trim()) ? value.trim() : null;

const titleCase = (value: string) =>
  value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

/** The category id a stored value maps to, or null when it is not one of ours. */
export const normalizeCategoryId = (value?: string | null): string | null => {
  if (value === null || value === undefined) return DEFAULT_CATEGORY_ID;
  const key = normalizeKey(String(value));
  if (!key) return DEFAULT_CATEGORY_ID;
  return ALIASES[key] ?? null;
};

/**
 * Everything needed to display an annotation's category. Known categories
 * always use the shared palette (stored colours on old notes came from the
 * previous palette). Unknown values keep their own name and, when valid, the
 * colour stored with them.
 */
export const getAnnotationCategory = (
  value?: string | null,
  storedColor?: string | null
): ResolvedAnnotationCategory => {
  const id = normalizeCategoryId(value);
  const known = id ? BY_ID.get(id) : undefined;
  if (known) return { ...known, known: true };

  const raw = String(value ?? '').trim();
  return {
    id: raw || 'unknown',
    label: raw ? titleCase(raw) : 'Other',
    color: safeHexColor(storedColor) ?? UNKNOWN_CATEGORY_COLOR,
    Icon: LabelOutlinedIcon,
    resolveColor: 'ResolveColorSand',
    locatorColor: 'WHITE',
    known: false,
  };
};

/** 'remove' or 'short' for a direction marked on the transcript, else null. */
export const directionCategoryOf = (value?: string | null): DirectionCategoryId | null => {
  if (value === null || value === undefined) return null;
  const id = normalizeCategoryId(value);
  return id === 'remove' || id === 'short' ? id : null;
};

/** Shorthand for the display colour of an annotation. */
export const categoryColor = (value?: string | null, storedColor?: string | null): string =>
  getAnnotationCategory(value, storedColor).color;

/** Hex colour -> rgba() with the given alpha, for tints. */
export const withAlpha = (hex: string, alpha: number): string => {
  const safe = safeHexColor(hex) ?? UNKNOWN_CATEGORY_COLOR;
  const full = safe.length === 4
    ? `#${safe[1]}${safe[1]}${safe[2]}${safe[2]}${safe[3]}${safe[3]}`
    : safe;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(1, Math.max(0, alpha))})`;
};
